import { useState } from "react";
import { toast } from "sonner";
import { Palette, Type, Download, Smartphone, ImageIcon, Copy, Check, Store, ShoppingBag, Sticker } from "lucide-react";
import { PageHeader } from "@/components/common";
import { BRAND, LEGAL_NAME, WEBSITE } from "@/lib/brand";
import { cn } from "@/lib/utils";

const COLORS = [
  { name: "Forest Green", hex: "#0B3D2E", note: "Primary background" },
  { name: "Emerald", hex: "#0F5132", note: "Surfaces / buttons" },
  { name: "Royal Gold", hex: "#E8C558", note: "Primary accent" },
  { name: "Deep Gold", hex: "#C9A227", note: "Accent shadow" },
  { name: "Ivory", hex: "#FAF7EF", note: "Light text / paper" },
  { name: "Charcoal", hex: "#161616", note: "Body text" },
];

const FONTS = [
  { name: "Cabinet Grotesk", role: "Display / Headings", weights: "700 · 800 · 900", sample: "1APPONLY" },
  { name: "IBM Plex Sans", role: "Body / UI", weights: "400 · 500 · 600 · 700", sample: "For All Your Needs" },
];

const CONSUMER = {
  key: "consumer",
  title: "Consumer App",
  subtitle: "1APPONLY — For All Your Needs",
  icon: ShoppingBag,
  appIcon: "/brandkit/consumer_icon_512.png",
  feature: "/brandkit/consumer_feature_1024x500.png",
  shots: [
    { src: "/brandkit/consumer_ss1_food.png", label: "Food ordering" },
    { src: "/brandkit/consumer_ss2_tracking.png", label: "Live tracking" },
    { src: "/brandkit/consumer_ss3_grocery.png", label: "Grocery" },
    { src: "/brandkit/consumer_ss4_ride.png", label: "Ride / Auto" },
    { src: "/brandkit/consumer_ss5_emergency.png", label: "Emergency" },
  ],
};

const VENDOR = {
  key: "vendor",
  title: "Vendor App",
  subtitle: "1APPONLY Vendor — Run your kitchen",
  icon: Store,
  appIcon: "/brandkit/vendor_icon_512.png",
  feature: "/brandkit/vendor_feature_1024x500.png",
  shots: [
    { src: "/brandkit/vendor_ss1_orders.png", label: "Live orders" },
    { src: "/brandkit/vendor_ss2_timer.png", label: "Prep timers" },
    { src: "/brandkit/vendor_ss3_print.png", label: "Bill printing" },
    { src: "/brandkit/vendor_ss4_menu.png", label: "Menu manager" },
  ],
};

function DownloadBtn({ href, name, children, className }) {
  return (
    <a
      href={href}
      download={name}
      data-testid={`download-${name}`}
      className={cn(
        "inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold border border-border bg-card hover:bg-muted transition-colors",
        className
      )}
    >
      <Download className="h-3.5 w-3.5" />
      {children || "Download"}
    </a>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="rounded-md border border-border bg-card">
      <div className="flex items-center gap-2.5 px-5 py-4 border-b border-border">
        <Icon className="h-4 w-4 text-primary" />
        <h3 className="font-display font-bold text-lg">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function AppAssets({ data }) {
  return (
    <Section title={data.title} icon={data.icon}>
      <p className="text-sm text-muted-foreground -mt-1 mb-5">{data.subtitle}</p>

      <div className="grid gap-6 md:grid-cols-2">
        {/* App icon */}
        <div>
          <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground font-medium mb-2">
            App Icon · 512×512
          </p>
          <div className="flex items-center gap-4">
            <img
              src={data.appIcon}
              alt={`${data.title} icon`}
              className="h-24 w-24 rounded-2xl object-cover ring-1 ring-black/10 shadow-sm"
              data-testid={`${data.key}-app-icon`}
            />
            <DownloadBtn href={data.appIcon} name={`${data.key}-icon-512.png`}>PNG 512</DownloadBtn>
          </div>
        </div>

        {/* Feature graphic */}
        <div>
          <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground font-medium mb-2">
            Feature Graphic · 1024×500
          </p>
          <img
            src={data.feature}
            alt={`${data.title} feature graphic`}
            className="w-full rounded-md ring-1 ring-black/10 shadow-sm"
            data-testid={`${data.key}-feature`}
          />
          <div className="mt-2">
            <DownloadBtn href={data.feature} name={`${data.key}-feature-1024x500.png`}>PNG 1024×500</DownloadBtn>
          </div>
        </div>
      </div>

      {/* Screenshots */}
      <div className="mt-7">
        <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground font-medium mb-3">
          Play Store Screenshots · 1080×1920
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {data.shots.map((s, i) => (
            <div key={s.src} className="group">
              <div className="relative overflow-hidden rounded-lg ring-1 ring-black/10 shadow-sm">
                <img src={s.src} alt={s.label} className="w-full" data-testid={`${data.key}-shot-${i}`} />
                <a
                  href={s.src}
                  download={`${data.key}-screenshot-${i + 1}.png`}
                  data-testid={`download-${data.key}-shot-${i}`}
                  className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <span className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-white text-black text-xs font-semibold">
                    <Download className="h-4 w-4" /> Download
                  </span>
                </a>
              </div>
              <p className="mt-1.5 text-xs text-center text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

export default function BrandKit() {
  const [copied, setCopied] = useState("");

  const copyHex = (hex) => {
    navigator.clipboard?.writeText(hex);
    setCopied(hex);
    toast.success(`${hex} copied`);
    setTimeout(() => setCopied(""), 1200);
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="brandkit-page">
      <PageHeader
        title="Brand Kit"
        subtitle="Logos, colours, type and Play Store assets for 1APPONLY"
        action={
          <a
            href="/brandkit/1APPONLY-Brand-Kit.pdf"
            download="1APPONLY-Brand-Kit.pdf"
            target="_blank"
            rel="noreferrer"
            data-testid="download-pdf-btn"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity shadow-sm"
          >
            <Download className="h-4 w-4" />
            Download full kit (PDF)
          </a>
        }
      />

      {/* Logo */}
      <Section title="Logo" icon={ImageIcon}>
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <div
            className="flex items-center justify-center rounded-xl p-6 shrink-0"
            style={{ backgroundColor: "#0B3D2E" }}
          >
            <img src={BRAND.square} alt="1APPONLY logo" className="h-40 w-auto" data-testid="brandkit-logo" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-muted-foreground">
              The official 1APPONLY emblem. Always keep clear space around it and place it on the deep
              forest-green background for best contrast. Do not stretch, recolour or add effects.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <DownloadBtn href={BRAND.square} name="1apponly-logo.webp">Logo (WEBP)</DownloadBtn>
              <DownloadBtn href="/brandkit/consumer_icon_512.png" name="1apponly-icon-512.png">Icon PNG 512</DownloadBtn>
            </div>
            <p className="mt-3 text-sm">
              <span className="text-muted-foreground">Website: </span>
              <a href="https://www.1apponly.com" target="_blank" rel="noreferrer" className="font-semibold text-primary" data-testid="brand-website">{WEBSITE}</a>
            </p>
          </div>
        </div>
      </Section>

      {/* Stickers */}
      <div className="mt-6">
        <Section title="Stickers" icon={Sticker}>
          <p className="text-sm text-muted-foreground -mt-1 mb-5">
            Ready-to-use sticker pack — round transparent cut-outs (WhatsApp-ready, 512×512 under 100&nbsp;KB) plus a square version.
          </p>
          <div className="flex flex-wrap items-end gap-6">
            {[
              { src: "/brandkit/sticker_round_512.png", label: "Round · 512 (transparent)", name: "1apponly-sticker-512.png", size: "h-28 w-28" },
              { src: "/brandkit/sticker_round_256.png", label: "Round · 256", name: "1apponly-sticker-256.png", size: "h-20 w-20" },
              { src: "/brandkit/sticker_tray_96.png", label: "Tray · 96", name: "1apponly-sticker-tray-96.png", size: "h-12 w-12" },
              { src: "/brandkit/sticker_square_512.png", label: "Square · 512", name: "1apponly-sticker-square-512.png", size: "h-28 w-28 rounded-xl" },
            ].map((s, i) => (
              <div key={s.src} className="flex flex-col items-center gap-2">
                <div
                  className="flex items-center justify-center p-2 rounded-xl"
                  style={{ backgroundImage: "repeating-conic-gradient(#e5e7eb 0% 25%, #ffffff 0% 50%)", backgroundSize: "16px 16px" }}
                >
                  <img src={s.src} alt={s.label} className={`object-contain ${s.size}`} data-testid={`sticker-${i}`} />
                </div>
                <p className="text-xs text-muted-foreground">{s.label}</p>
                <DownloadBtn href={s.src} name={s.name}>PNG</DownloadBtn>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* Colors */}
      <div className="mt-6">
        <Section title="Colour Palette" icon={Palette}>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {COLORS.map((c) => {
              const isCopied = copied === c.hex;
              return (
                <button
                  key={c.hex}
                  onClick={() => copyHex(c.hex)}
                  data-testid={`color-${c.hex.replace("#", "")}`}
                  className="text-left rounded-lg border border-border overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="h-20 w-full flex items-start justify-end p-2" style={{ backgroundColor: c.hex }}>
                    {isCopied ? (
                      <Check className="h-4 w-4 text-white/90" />
                    ) : (
                      <Copy className="h-4 w-4 text-white/50" />
                    )}
                  </div>
                  <div className="p-2.5">
                    <p className="text-sm font-semibold leading-none">{c.name}</p>
                    <p className="text-xs font-mono text-muted-foreground mt-1">{c.hex}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{c.note}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </Section>
      </div>

      {/* Typography */}
      <div className="mt-6">
        <Section title="Typography" icon={Type}>
          <div className="grid gap-4 sm:grid-cols-2">
            {FONTS.map((f) => (
              <div key={f.name} className="rounded-lg border border-border p-5">
                <p
                  className={cn("leading-none", f.name === "Cabinet Grotesk" ? "font-display font-black text-4xl" : "font-semibold text-4xl")}
                >
                  {f.sample}
                </p>
                <div className="mt-4">
                  <p className="text-sm font-semibold">{f.name}</p>
                  <p className="text-xs text-muted-foreground">{f.role} · {f.weights}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* App assets */}
      <div className="mt-6 space-y-6">
        <AppAssets data={CONSUMER} />
        <AppAssets data={VENDOR} />
      </div>

      <p className="mt-8 text-center text-[11px] text-muted-foreground/70">
        © {new Date().getFullYear()} {LEGAL_NAME}. All brand assets are property of the company.
      </p>
    </div>
  );
}
